import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useTheme } from "@/context/ThemeContext";
import { COMPANY_LOGO } from "@/lib/api";
import RatesWidget from "@/components/RatesWidget";
import {
  LayoutDashboard, TrendingUp, TrendingDown, Landmark, Users,
  CalendarClock, Package, FileText, PieChart, Settings, LogOut, Repeat, Sun, Moon, Receipt, CreditCard, CalendarDays, Wallet, Activity as ActivityIcon, Mail as MailIcon,
} from "lucide-react";

const nav = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/ajanda", label: "Ajanda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/gelirler", label: "Gelirler", icon: TrendingUp, testid: "nav-income" },
  { to: "/giderler", label: "Giderler", icon: TrendingDown, testid: "nav-expenses" },
  { to: "/banka", label: "Banka Hesapları", icon: Landmark, testid: "nav-banks" },
  { to: "/kasa", label: "Kasa Takibi", icon: Wallet, testid: "nav-kasa" },
  { to: "/cari", label: "Cari Hesaplar", icon: Users, testid: "nav-cari" },
  { to: "/cek", label: "Çek Takibi", icon: Receipt, testid: "nav-cheques" },
  { to: "/kredi", label: "Kredi Takibi", icon: CreditCard, testid: "nav-loans" },
  { to: "/odemeler", label: "Ödemeler & Tahsilat", icon: CalendarClock, testid: "nav-upcoming" },
  { to: "/tekrarlayan", label: "Tekrarlayan Kayıt", icon: Repeat, testid: "nav-recurring" },
  { to: "/urunler", label: "Ürünler", icon: Package, testid: "nav-products" },
  { to: "/proforma", label: "Proforma Fatura", icon: FileText, testid: "nav-proforma" },
  { to: "/raporlar", label: "Raporlar", icon: PieChart, testid: "nav-reports" },
  { to: "/aktivite", label: "Aktivite Log", icon: ActivityIcon, testid: "nav-activity" },
  { to: "/mail-imzasi", label: "Mail İmzası", icon: MailIcon, testid: "nav-signature" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav_r = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
          <img src={COMPANY_LOGO} alt="MADYMARK" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-display font-extrabold text-slate-900 dark:text-slate-100 leading-tight">MADYMARK</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">Finans Merkezi</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 mx-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#1E2A9C] text-white shadow-sm shadow-[#1E2A9C]/20"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100 dark:border-slate-800">
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-2 truncate">{user?.email}</div>
          <button
            data-testid="btn-logout"
            onClick={async () => { await logout(); nav_r("/login"); }}
            className="w-full flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-red-600 transition"
          >
            <LogOut className="w-4 h-4" /> Çıkış
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">
              MADYMARK İÇ VE DIŞ TİCARET SANAYİ A.Ş
            </div>
            <div className="text-sm text-slate-700 dark:text-slate-300">VKN 6101146549 · Başakşehir V.D.</div>
          </div>
          <div className="flex items-center gap-3">
            <RatesWidget />
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition"
              title={theme === "dark" ? "Aydınlık Mod" : "Karanlık Mod"}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-full p-1">
              {["TRY", "USD", "EUR"].map((c) => (
                <button
                  key={c}
                  data-testid={`currency-${c}`}
                  onClick={() => setCurrency(c)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold font-mono-num transition ${
                    currency === c ? "bg-[#1E2A9C] text-white shadow" : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  {c === "TRY" ? "₺ TRY" : c === "USD" ? "$ USD" : "€ EUR"}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div className="p-8 fade-up">{children}</div>
      </main>
    </div>
  );
}
